import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { PlanwertFilter } from '../../service/planwert/planwert-filter';
import { debounceTime, map, switchMap, startWith, takeWhile } from 'rxjs/operators';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, Observable } from 'rxjs';
import { PlanwertStatus, PlanwertStatusItem } from '../../domain/model/planwert-status.enum';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { PlanwertTypeItem } from '../../domain/model/planwert-type.enum';
import { PlanwertExportService } from '../../service/planwert/planwert-export.service';
import { PlanwertService } from '../../service/planwert/planwert.service';

const FILTER_DEBOUNCE_TIME = 250;
const FILTER_OPTION_LIMIT = 50;

@Component({
    selector: 'redispatch-planwert-filter',
    templateUrl: './planwert-filter.component.html',
    styleUrls: [
        './planwert-filter.component.scss',
    ],
    standalone: false,
})
export class PlanwertFilterComponent implements OnInit, OnChanges, OnDestroy {

    @Input() initialFilter?: PlanwertFilter;
    @Output() filter = new EventEmitter<PlanwertFilter>();

    stadtwerkeId?: string;

    public filterFormGroup: UntypedFormGroup;

    filteredDokumentenId$: Observable<string[]>;
    filteredTechnischeResourceI$: Observable<string[]>;
    filteredBezVnbIntern$: Observable<string[]>;
    filteredNetzverknuepfungspunkte$: Observable<string[]>;

    public readonly PlanwertStatusItems: PlanwertStatusItem[] = PlanwertStatusItem.getAll();
    public readonly PlanwertTypeItems: PlanwertTypeItem[] = PlanwertTypeItem.getAll();

    private componentDestroyed = false;

    private formBuilder = inject(FormBuilder);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private planwertService = inject(PlanwertService);
    private planwertExportService = inject(PlanwertExportService);

    constructor() {
        // Build form
        this.filterFormGroup = this.formBuilder.group({
            erzeugungsanlageDokumentenId: [
                '',
            ],
            erzeugungsanlageTechnischeResourceId: [
                '',
            ],
            erzeugungsanlageBezVnbIntern: [
                '',
            ],
            netzzuordnungNetzverknuepfungspunkte: [
                '',
            ],
            dataTransferDate: this.formBuilder.group({
                start: [
                    '',
                ],
                end: [
                    '',
                ],
            }),
            type: [
                '',
            ],
            status: [
                '',
            ],
        });
    }

    async ngOnInit() {
        this.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();

        this.filterFormGroup.valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.value),
                takeWhile(() => !this.componentDestroyed),
                map(val => {
                    return new PlanwertFilter(
                        null,
                        this.stadtwerkeId,
                        val.erzeugungsanlageDokumentenId,
                        val.erzeugungsanlageTechnischeResourceId,
                        val.erzeugungsanlageBezVnbIntern,
                        val.netzzuordnungNetzverknuepfungspunkte,
                        val.dataTransferDate,
                        val.type,
                        val.status,
                    );
                }),
            )
            .subscribe(val => this.filter.next(val));

        this.filteredDokumentenId$ = this.filterFormGroup.get('erzeugungsanlageDokumentenId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('erzeugungsanlageDokumentenId').value),
                distinctUntilChanged(),
                switchMap(id => this.planwertService.listDokumentenIds(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredTechnischeResourceI$ = this.filterFormGroup.get('erzeugungsanlageTechnischeResourceId').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('erzeugungsanlageTechnischeResourceId').value),
                distinctUntilChanged(),
                switchMap(id => this.planwertService.listTechnischeResourceId(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredBezVnbIntern$ = this.filterFormGroup.get('erzeugungsanlageBezVnbIntern').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('erzeugungsanlageBezVnbIntern').value),
                distinctUntilChanged(),
                switchMap(id => this.planwertService.listBezVnbIntern(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );

        this.filteredNetzverknuepfungspunkte$ = this.filterFormGroup.get('netzzuordnungNetzverknuepfungspunkte').valueChanges
            .pipe(
                debounceTime(FILTER_DEBOUNCE_TIME),
                startWith(this.filterFormGroup.get('netzzuordnungNetzverknuepfungspunkte').value),
                distinctUntilChanged(),
                switchMap(id => this.planwertService.listNetzverknuepfungspunkte(id, this.stadtwerkeId)),
                map(options => options.slice(0, FILTER_OPTION_LIMIT)),
            );
    }

    ngOnChanges(): void {
        this.filterFormGroup.patchValue(this.initialFilter);
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    planwertStatusToClass(status: PlanwertStatus): string {
        switch (status) {
            case PlanwertStatus.OPEN:
                return 'open';
            case PlanwertStatus.PROCESSED:
                return 'success';
            case PlanwertStatus.SYSTEM_FAILED:
            case PlanwertStatus.INVALID:
                return 'failed';

        }
    }

    public exportPlanwerte() {
        this.planwertExportService.exportPlanwerteCSV(
            this.teilgebietSelectionService.getSelectedTeilgebiet(),
        );
    }
}
